// src/components/itinerary/ActivityForm.tsx
import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  DialogActions,
  InputAdornment,
  FormHelperText
} from '@mui/material';
import { Activity } from '../../contexts/ItineraryContext';
import { v4 as uuidv4 } from 'uuid';

interface ActivityFormProps {
  activity?: Activity;
  destination: string;
  onSave: (activity: Activity) => void;
  onCancel: () => void;
}

const ActivityForm: React.FC<ActivityFormProps> = ({
  activity,
  destination,
  onSave,
  onCancel
}) => {
  const isEdit = !!activity;
  
  const [formData, setFormData] = useState<Partial<Activity>>(
    activity || {
      title: '',
      description: '',
      location: destination,
      type: 'activity',
      cost: undefined,
      duration: ''
    }
  );
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setFormData({
      ...formData,
      [name]: name === 'cost' ? (value ? parseFloat(value) : undefined) : value
    });
  };

  const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name as string]: value
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.location?.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (formData.cost !== undefined && (formData.cost < 0 || isNaN(formData.cost))) {
      newErrors.cost = 'Cost must be a valid number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    const newActivity: Activity = {
      id: activity?.id || uuidv4(),
      title: formData.title || '',
      description: formData.description || '',
      location: formData.location || destination,
      type: formData.type as 'activity' | 'food' | 'accommodation' | 'transportation',
      cost: formData.cost,
      duration: formData.duration,
      // Use the existing image URL or generate a generic one based on type
      imageUrl: activity?.imageUrl || `https://source.unsplash.com/random/?${formData.type || 'travel'}`
    };
    
    onSave(newActivity);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ pt: 1 }}>
        <TextField
          fullWidth
          label="Title"
          name="title"
          value={formData.title || ''}
          onChange={handleInputChange}
          margin="normal"
          error={!!errors.title}
          helperText={errors.title}
          required
        />
        
        <TextField
          fullWidth
          label="Description"
          name="description"
          value={formData.description || ''}
          onChange={handleInputChange}
          margin="normal"
          multiline
          rows={3}
        />
        
        <TextField
          fullWidth
          label="Location"
          name="location"
          value={formData.location || ''}
          onChange={handleInputChange}
          margin="normal"
          error={!!errors.location}
          helperText={errors.location}
          required
        />
        
        <FormControl fullWidth margin="normal">
          <InputLabel id="activity-type-label">Activity Type</InputLabel>
          <Select
            labelId="activity-type-label"
            name="type"
            value={formData.type || 'activity'}
            onChange={handleSelectChange as any}
            label="Activity Type"
          >
            <MenuItem value="activity">Activity</MenuItem>
            <MenuItem value="food">Food</MenuItem>
            <MenuItem value="accommodation">Accommodation</MenuItem>
            <MenuItem value="transportation">Transportation</MenuItem>
          </Select>
        </FormControl>
        
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <TextField
            label="Cost"
            name="cost"
            type="number"
            value={formData.cost === undefined ? '' : formData.cost}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            error={!!errors.cost}
            helperText={errors.cost}
            sx={{ width: '50%' }}
          />
          
          <TextField
            label="Duration"
            name="duration"
            placeholder="e.g. 2 hours"
            value={formData.duration || ''}
            onChange={handleInputChange}
            sx={{ width: '50%' }}
          />
        </Box>
      </Box>
      
      <DialogActions sx={{ px: 0, pt: 3 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary">
          {isEdit ? 'Update Activity' : 'Add Activity'}
        </Button>
      </DialogActions>
    </form>
  );
};

export default ActivityForm;